import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiTrash2, FiPlus, FiMail } from "react-icons/fi";

import Overlay from "../Overlay";
import Modal from "../Utils/Modal";
import FilterContainer from "../Utils/FilterContainer";
import { useModal } from "../../contexts/ModalContext";
import useApi from "../../hooks/useApi";

import ModalNovoEmailTransferencia from "./ModalNovoEmailTransferencia";
import ConfirmDeleteEmailTransferencia from "./ConfirmDeleteEmailTransferencia";

const MODAL_NEW = "modalAddEmailTransferencia";
const MODAL_DELETE = "modalDeleteEmailTransferencia";

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: #0f766e;
    }
`;

const BotaoNovo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: #0f766e;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color .15s ease, transform .15s ease;

    &:hover { background-color: #0d6660; transform: translateY(-1px); }
    &:focus-visible { outline: 2px solid #0f766e; outline-offset: 2px; }
`;

const FiltroWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
`;

const ListaWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-right: 4px;
`;

const Grupo = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background-color: #fff;
`;

const GrupoHeader = styled.div`
    background-color: #f1f5f9;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.3px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    span.qtd {
        font-size: 11px;
        background-color: #0f766e;
        color: #fff;
        padding: 2px 8px;
        border-radius: 999px;
        font-weight: 600;
    }
`;

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;

    td {
        padding: 9px 14px;
        font-size: 13px;
        color: #1e293b;
        border-top: 1px solid #f1f5f9;
    }

    tr:first-child td { border-top: none; }

    td.acao {
        width: 50px;
        text-align: center;
    }
`;

const BotaoLixeira = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    color: #b91c1c;
    cursor: pointer;
    transition: background-color .15s ease, transform .15s ease;

    &:hover {
        background-color: rgba(185, 28, 28, 0.12);
        transform: scale(1.05);
    }
    &:focus-visible { outline: 2px solid #e11d48; outline-offset: 2px; }
`;

const Vazio = styled.div`
    padding: 28px 16px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    background-color: #f8fafc;
`;

const Carregando = styled(Vazio)`
    color: #475569;
`;

const ModalEmailsTransferencia = ({ obras = [], onClose }) => {

    const { closeModal, openModal, isModalOpen } = useModal();
    const api = useApi();

    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filtroCC, setFiltroCC] = useState([]);
    const [filtroEmail, setFiltroEmail] = useState("");

    const [emailToDelete, setEmailToDelete] = useState(null);

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            closeModal();
        }
    };

    const fetchEmails = useCallback(async () => {

        setLoading(true);

        try {

            const { data } = await api.get("/Painel/API/Transferencia/GetEmails/");
            setEmails(Array.isArray(data) ? data : []);

        } catch (error) {

            console.error("Erro ao listar e-mails:", error);
            setEmails([]);

        } finally {

            setLoading(false);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchEmails();
    }, [fetchEmails]);

    const ccsDoFiltro = useMemo(() => {
        const set = new Set(emails.map((e) => e.cc).filter(Boolean));
        if (set.size === 0) return obras ?? [];
        return [...set].sort();
    }, [emails, obras]);

    const grupos = useMemo(() => {

        const ccsSet = new Set((filtroCC || []).map((o) => o.value));
        const termo = (filtroEmail || "").trim().toLowerCase();

        const filtrados = emails.filter((e) => {
            if (ccsSet.size && !ccsSet.has(e.cc)) return false;
            if (termo && !String(e.email || "").toLowerCase().includes(termo)) return false;
            return true;
        });

        const map = new Map();
        for (const item of filtrados) {
            const cc = item.cc || "—";
            if (!map.has(cc)) map.set(cc, []);
            map.get(cc).push(item);
        }

        return [...map.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([cc, lista]) => ({ cc, lista }));

    }, [emails, filtroCC, filtroEmail]);

    const handleAbrirNovo = () => openModal(MODAL_NEW);

    const handleAbrirDelete = (item) => {
        setEmailToDelete(item);
        openModal(MODAL_DELETE);
    };

    const handleFecharDelete = () => {
        setEmailToDelete(null);
        closeModal();
    };

    return (
        <>
            <Overlay state={ true } >
                <Modal header close={ handleClose } size="lg" >
                    <Container>

                        <Header>
                            <Titulo>
                                <FiMail size={ 20 } />
                                E-MAILS DE NOTIFICAÇÃO DE TRANSFERÊNCIA
                            </Titulo>
                            <BotaoNovo type="button" onClick={ handleAbrirNovo } >
                                <FiPlus size={ 16 } /> NOVO E-MAIL
                            </BotaoNovo>
                        </Header>

                        <FiltroWrapper>
                            <FilterContainer
                                type="select"
                                placeholder="CENTRO DE CUSTO"
                                optType="obj"
                                options={ ccsDoFiltro }
                                changeFunc={ setFiltroCC }
                            />
                            <FilterContainer
                                type="text"
                                placeholder="E-MAIL"
                                valor={ filtroEmail }
                                changeFunc={ setFiltroEmail }
                            />
                        </FiltroWrapper>

                        <ListaWrapper>
                            { loading ? (
                                <Carregando>Carregando e-mails...</Carregando>
                            ) : grupos.length === 0 ? (
                                <Vazio>
                                    Nenhum e-mail cadastrado para os filtros selecionados.
                                    <br />
                                    Clique em <strong>NOVO E-MAIL</strong> para começar.
                                </Vazio>
                            ) : (
                                grupos.map(({ cc, lista }) => (
                                    <Grupo key={ cc } >
                                        <GrupoHeader>
                                            <span>{ cc }</span>
                                            <span className="qtd">
                                                { lista.length } { lista.length === 1 ? "e-mail" : "e-mails" }
                                            </span>
                                        </GrupoHeader>
                                        <Tabela>
                                            <tbody>
                                                { lista.map((item) => (
                                                    <tr key={ item.id } >
                                                        <td>{ item.email }</td>
                                                        <td className="acao">
                                                            <BotaoLixeira
                                                                type="button"
                                                                title="Remover e-mail"
                                                                aria-label="Remover e-mail"
                                                                onClick={ () => handleAbrirDelete(item) }
                                                            >
                                                                <FiTrash2 size={ 16 } />
                                                            </BotaoLixeira>
                                                        </td>
                                                    </tr>
                                                )) }
                                            </tbody>
                                        </Tabela>
                                    </Grupo>
                                ))
                            ) }
                        </ListaWrapper>

                    </Container>
                </Modal>
            </Overlay>

            { isModalOpen(MODAL_NEW) && (
                <ModalNovoEmailTransferencia
                    obras={ obras }
                    onCreated={ fetchEmails }
                />
            ) }

            { isModalOpen(MODAL_DELETE) && (
                <ConfirmDeleteEmailTransferencia
                    item={ emailToDelete }
                    fecharModal={ handleFecharDelete }
                    onDelete={ fetchEmails }
                />
            ) }
        </>
    );

};

export default ModalEmailsTransferencia;
